/**
 * Digital SAT — Landing page.
 *
 * Route: /resources/digital-sat
 *
 * Fetches available Winhouse tests from the shared Exam Engine and groups
 * them by test type (topic / short_section / full_section / full_mock).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Timer, PlayCircle, ClipboardList, GraduationCap, Sparkles, ArrowRight, ShieldCheck, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import DSATDailyChallengeWidget from '@/components/dsat/DSATDailyChallengeWidget';
import DSATAnalyticsPanel from '@/components/dsat/DSATAnalyticsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const GROUPS = [
  { key: 'topic', label: 'Topic Practice', icon: Sparkles, hint: 'Focus one skill at a time.' },
  { key: 'short_section', label: 'Short Section Practice', icon: PlayCircle, hint: 'Quick mixed-section sets.' },
  { key: 'full_section', label: 'Full Section', icon: ClipboardList, hint: 'Full-length RW or Math section.' },
  { key: 'full_mock', label: 'Full Digital SAT Mock', icon: GraduationCap, hint: 'Winhouse Digital SAT Mock #1.' },
];

export default function DigitalSATLandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tests, setTests] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      // Public catalogue always loads. Private slices only when logged in.
      // Each private call is caught individually so a stale token doesn't
      // trigger a red toast for anonymous visitors.
      const catalogue = ax.get('/api/exam/v2/tests?exam_type=dsat')
        .then((r) => r.data?.items || [])
        .catch(() => []);
      const attemptsP = user
        ? ax.get('/api/exam/v2/me/attempts?exam_type=dsat&limit=20').then((r) => r.data?.items || []).catch(() => [])
        : Promise.resolve([]);
      const dashboardP = user
        ? ax.get('/api/exam/v2/me/dashboard?exam_type=dsat').then((r) => r.data || null).catch(() => null)
        : Promise.resolve(null);
      const assignmentsP = user
        ? ax.get('/api/exam/v2/me/assignments').then((r) => r.data?.items || []).catch(() => [])
        : Promise.resolve([]);
      const [t, a, d, asg] = await Promise.all([catalogue, attemptsP, dashboardP, assignmentsP]);
      if (!live) return;
      setTests(t);
      setAttempts(a);
      setDashboard(d);
      setAssignments(asg);
      setLoading(false);
    })();
    return () => { live = false; };
  }, [user]);

  const grouped = GROUPS.map((g) => ({ ...g, items: tests.filter((t) => t.test_type === g.key) }));

  const requireLogin = (redirect) => {
    toast.message('Sign in to start this test — your progress will be saved to your Winhouse account.');
    nav(`/student/login?next=${encodeURIComponent(redirect || '/resources/digital-sat')}`);
  };

  const startTest = async (test, mode) => {
    if (!user) { requireLogin('/resources/digital-sat'); return; }
    try {
      const r = await ax.post('/api/exam/v2/attempts/start', {
        test_id: test.test_id, mode: test.test_type === 'full_mock' ? 'timed' : mode,
      });
      nav(`/dsat/attempt/${r.data.attempt_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start test'); }
  };

  const startAssignment = async (assignment) => {
    if (!user) { requireLogin('/resources/digital-sat'); return; }
    try {
      const r = await ax.post('/api/exam/v2/attempts/start', {
        test_id: assignment.test_id,
        mode: assignment.mode === 'untimed' ? 'untimed' : 'timed',
        assignment_id: assignment.assignment_id,
      });
      nav(`/dsat/attempt/${r.data.attempt_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start assignment'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        <div className="min-h-[60vh] flex items-center justify-center text-gray-500 pt-24"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading Digital SAT catalogue…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="dsat-landing-wrap">
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <div className="max-w-6xl mx-auto px-4 pt-28 md:pt-32 pb-10" data-testid="dsat-landing">
        {/* Hero */}
        <div className="mb-8">
          <Link to="/resources" className="text-gray-500 hover:text-gray-800 text-xs inline-flex items-center gap-1 mb-4" data-testid="dsat-back-to-resources">
            <ArrowRight className="h-3 w-3 rotate-180" /> Back to Resources
          </Link>
          <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-2">Digital SAT-style practice</h1>
          <p className="text-sm text-gray-600 mt-3 max-w-2xl">
            Winhouse Digital SAT practice sets, full sections, and adaptive-style full mocks. All content is authored
            by Winhouse Tutors — original items in the Digital SAT format. Not affiliated with the College Board.
          </p>
        </div>

      {/* Admin/tutor quick-links — role-gated */}
      {user?.role === 'admin' && (
        <Card className="p-3 mb-6 bg-gray-900 text-white" data-testid="dsat-admin-quicklinks">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="h-4 w-4 text-[#D3D86F]" />
            <span className="text-[11px] uppercase tracking-wider font-bold text-[#D3D86F]">Admin quick links</span>
            <Link to="/admin/v5"><Button size="sm" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20">Question Bank</Button></Link>
            <Link to="/admin/v5"><Button size="sm" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20">Test Builder</Button></Link>
            <Link to="/admin/v5"><Button size="sm" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20">Assignments</Button></Link>
          </div>
        </Card>
      )}
      {user?.role === 'tutor' && (
        <Card className="p-3 mb-6 bg-gray-900 text-white" data-testid="dsat-tutor-quicklinks">
          <div className="flex items-center gap-2 flex-wrap">
            <GraduationCap className="h-4 w-4 text-[#D3D86F]" />
            <span className="text-[11px] uppercase tracking-wider font-bold text-[#D3D86F]">Tutor workspace</span>
            <Link to="/tutor/dsat"><Button size="sm" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20" data-testid="dsat-tutor-open">Open Tutor DSAT →</Button></Link>
          </div>
        </Card>
      )}

      {/* Daily Challenge */}
      <div className="mb-8" data-testid="dsat-daily-challenge-slot">
        <DSATDailyChallengeWidget />
      </div>

      {/* Assignments strip */}
      {assignments.length > 0 && (
        <Card className="p-5 mb-8 bg-amber-50 border-amber-200" data-testid="dsat-my-assignments">
          <div className="flex items-baseline gap-2 mb-3">
            <ClipboardList className="h-4 w-4 text-amber-700" />
            <h2 className="text-base font-bold text-gray-900">My Assignments</h2>
            <span className="text-[11px] text-gray-500">Tests assigned by your tutor.</span>
          </div>
          <div className="space-y-2">
            {assignments.filter((a) => ['assigned', 'in_progress'].includes(a.status)).map((a) => {
              const used = a.attempts_used || 0;
              const remaining = Math.max(0, (a.attempt_limit || 1) - used);
              return (
                <div key={a.assignment_id} className="flex flex-wrap items-center gap-3 p-3 bg-white border border-amber-100 rounded"
                     data-testid={`dsat-assignment-${a.assignment_id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-bold text-gray-900 text-sm">{a.test_title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      Assigned by {a.assigned_by_name || a.assigned_by} · {(a.test_type || '').replace('_', ' ')} · {a.mode.toUpperCase()}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {a.due_at ? <>Due <b>{new Date(a.due_at).toLocaleString()}</b></> : 'No deadline'}
                      &nbsp;·&nbsp; Attempts: {used}/{a.attempt_limit} ({remaining} left)
                      &nbsp;·&nbsp; Status: <Badge className={
                        a.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-amber-200 text-amber-900'
                      }>{a.status}</Badge>
                    </div>
                    {a.instructions && <div className="text-[11px] text-gray-700 mt-1 italic">&ldquo;{a.instructions}&rdquo;</div>}
                  </div>
                  <Button size="sm" onClick={() => startAssignment(a)} disabled={remaining <= 0}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`dsat-assignment-start-${a.assignment_id}`}>
                    {a.status === 'in_progress' ? 'Continue' : 'Start'} <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              );
            })}
            {assignments.filter((a) => ['assigned', 'in_progress'].includes(a.status)).length === 0 && (
              <div className="text-xs text-gray-500 italic">All assignments completed. Great work!</div>
            )}
          </div>
        </Card>
      )}

      {/* Dashboard mini-stats */}
      {dashboard && dashboard.total_attempts > 0 && (
        <Card className="p-5 mb-8 bg-red-50/40 border-red-100" data-testid="dsat-dashboard-strip">
          <div className="flex flex-wrap items-end gap-6">
            <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Attempts</div><div className="text-2xl font-bold text-gray-900">{dashboard.total_attempts}</div></div>
            <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Average</div><div className="text-2xl font-bold text-red-700">{dashboard.average_pct}%</div></div>
            <div><div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Best</div><div className="text-2xl font-bold text-green-700">{dashboard.best_pct}%</div></div>
            <div className="ml-auto"><Link to="/dsat/attempts" className="text-sm text-red-700 underline" data-testid="dsat-goto-attempts">Attempt history →</Link></div>
          </div>
        </Card>
      )}

      {/* Full analytics dashboard */}
      {user && dashboard && dashboard.total_attempts > 0 && (
        <div className="mb-8" data-testid="dsat-analytics-slot">
          <DSATAnalyticsPanel />
        </div>
      )}

      {/* Groups */}
      <div className="space-y-8">
        {grouped.map((g) => (
          <section key={g.key} data-testid={`dsat-group-${g.key}`}>
            <div className="flex items-baseline gap-3 mb-3">
              <g.icon className="h-4 w-4 text-red-700" />
              <h2 className="text-lg font-bold text-gray-900">{g.label}</h2>
              <span className="text-xs text-gray-500">{g.hint}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {g.items.map((t) => (
                <Card key={t.test_id} className="p-4 border-gray-200 hover:border-red-300 transition-colors" data-testid={`dsat-test-card-${t.test_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900">{t.title}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{t.description}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {t.section && <Badge className="text-[10px] bg-red-700 hover:bg-red-700">{t.section === 'rw' ? 'Reading & Writing' : 'Math'}</Badge>}
                        {t.time_limit_minutes && <Badge variant="outline" className="text-[10px]"><Timer className="h-3 w-3 mr-1" />{t.time_limit_minutes} min</Badge>}
                        {t.question_count && <Badge variant="outline" className="text-[10px]">{t.question_count} Q</Badge>}
                        {t.test_type === 'full_mock' && <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">Adaptive practice</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    {t.allow_untimed && t.test_type !== 'full_mock' && (
                      <Button size="sm" variant="outline" onClick={() => startTest(t, 'untimed')} data-testid={`dsat-start-untimed-${t.test_id}`}>Start untimed</Button>
                    )}
                    <Button size="sm" onClick={() => startTest(t, 'timed')} className="bg-red-700 hover:bg-red-800 text-white ml-auto" data-testid={`dsat-start-timed-${t.test_id}`}>
                      {t.test_type === 'full_mock' ? 'Start mock' : 'Start timed'} <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Recent attempts */}
      {attempts.length > 0 && (
        <section className="mt-10" data-testid="dsat-recent-attempts">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Recent attempts</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr><th className="p-2 text-left">Test</th><th className="p-2">Mode</th><th className="p-2">Started</th><th className="p-2">Score</th><th className="p-2"></th></tr>
              </thead>
              <tbody>
                {attempts.slice(0, 8).map((a) => (
                  <tr key={a.attempt_id} className="border-t border-gray-100">
                    <td className="p-2">{a.test_title}</td>
                    <td className="p-2 text-center"><Badge variant="outline" className="text-[10px]">{a.mode}</Badge></td>
                    <td className="p-2 text-center text-gray-500 text-xs">{(a.started_at || '').split('T')[0]}</td>
                    <td className="p-2 text-center font-mono">{a.score ? `${a.score.percentage}%` : '—'}</td>
                    <td className="p-2 text-right">
                      <Link to={`/dsat/attempt/${a.attempt_id}${a.status === 'submitted' ? '/review' : ''}`} className="text-red-700 text-xs underline" data-testid={`dsat-open-${a.attempt_id}`}>
                        {a.status === 'submitted' ? 'Review' : 'Resume'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="mt-10 text-[11px] text-gray-500 leading-relaxed">
        <strong>Content notice.</strong> All Winhouse Digital SAT questions are original. This platform is not
        affiliated with, endorsed by, or a substitute for the College Board&apos;s official SAT or Bluebook software.
      </div>
      </div>
      <Footer />
    </div>
  );
}
